import { type ImportField, type ImportKind, importFields } from "@gtm/contracts/imports";
import { csvCell } from "@/lib/csv";

export const nouns: Record<ImportKind, [string, string]> = {
  signals: ["message", "messages"],
  people: ["person", "people"],
  companies: ["company", "companies"],
};
export function count(n: number, kind: ImportKind | "row") {
  const [one, many] = kind === "row" ? ["row", "rows"] : nouns[kind];
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

const signalOnly: ImportField[] = ["message_url", "message_text", "occurred_at", "notes"];
const personOnly: ImportField[] = [
  "author_name",
  "author_id",
  "author_url",
  "email",
  "linkedin_url",
  "person_id",
];
/** The fields an import of this kind reads, most useful first. */
export const primaryFields: Record<ImportKind, ImportField[]> = {
  signals: [
    "message_text",
    "message_url",
    "occurred_at",
    "author_name",
    "author_url",
    "company_name",
    "company_domain",
  ],
  people: [
    "author_name",
    "email",
    "linkedin_url",
    "author_url",
    "company_name",
    "company_domain",
    "external_id",
  ],
  companies: ["company_name", "company_domain", "external_id"],
};
export function extraFields(kind: ImportKind) {
  return importFields.filter(
    (f) =>
      !primaryFields[kind].includes(f) &&
      (kind === "signals" || !signalOnly.includes(f)) &&
      (kind !== "companies" || !personOnly.includes(f)),
  );
}

export function fieldLabel(field: ImportField, kind: ImportKind) {
  switch (field) {
    case "source":
      return "Source name";
    case "external_id":
      return kind === "signals" ? "Message ID" : "Your record ID";
    case "message_url":
      return "Message URL";
    case "message_text":
      return "Message";
    case "occurred_at":
      return "Sent at";
    case "author_name":
      return kind === "people" ? "Name" : "Author name";
    case "author_id":
      return "Author ID";
    case "author_url":
      return "Profile URL";
    case "email":
      return "Email";
    case "linkedin_url":
      return "LinkedIn URL";
    case "person_id":
      return "Galaxy person ID";
    case "company_name":
      return kind === "companies" ? "Name" : "Company name";
    case "company_domain":
      return kind === "companies" ? "Domain" : "Company domain";
    case "company_id":
      return "Galaxy company ID";
    case "notes":
      return "Notes";
  }
}
export function fieldHint(field: ImportField, kind: ImportKind) {
  switch (field) {
    case "source":
      return "Sets the source per row, overriding the default";
    case "external_id":
      return "A stable ID from your system, so re-imports update instead of duplicate";
    case "message_url":
      return "Link to the original message";
    case "occurred_at":
      return "ISO date with timezone, e.g. 2026-09-18T14:30:00-04:00";
    case "author_id":
      return "The author’s user ID on the platform";
    case "author_url":
      return "Profile link used to recognise the person next time";
    case "email":
      return "Used to suggest existing people";
    case "person_id":
    case "company_id":
      return "Links rows directly to an existing record";
    case "company_domain":
      return kind === "companies" ? "e.g. acme.com" : "Used to find or create the company";
    default:
      return "";
  }
}
export function isRequired(field: ImportField, kind: ImportKind) {
  return kind === "companies"
    ? field === "company_name"
    : field === "author_name" || (kind === "signals" && field === "message_text");
}

type Mapping = Partial<Record<ImportField, string>>;
/** Column combinations every row needs, phrased as what to map next. */
export function missingColumns(kind: ImportKind, mapping: Mapping) {
  const has = (...fields: ImportField[]) => fields.some((f) => mapping[f]);
  const name = (f: ImportField) => fieldLabel(f, kind);
  const missing: string[] = [];
  for (const field of importFields)
    if (isRequired(field, kind) && !mapping[field]) missing.push(`Map a ${name(field)} column.`);
  if (kind === "companies" && !has("company_domain", "external_id"))
    missing.push("Map a Domain or Your record ID column so companies can be recognised.");
  if (kind === "people" && !has("author_url", "author_id", "external_id", "person_id"))
    missing.push(
      "Map a Profile URL, Author ID or Your record ID column so people can be recognised.",
    );
  if (kind === "signals" && !has("message_url", "external_id"))
    missing.push("Map a Message URL or Message ID column.");
  if (kind === "signals" && !has("author_url", "author_id", "person_id"))
    missing.push("Map a Profile URL or Author ID column so new authors can be created.");
  return missing;
}

const templates: Record<ImportKind, ImportField[]> = {
  signals: [
    "message_url",
    "message_text",
    "occurred_at",
    "author_name",
    "author_url",
    "company_name",
    "company_domain",
    "notes",
  ],
  people: ["external_id", "author_name", "email", "linkedin_url", "company_name", "company_domain"],
  companies: ["external_id", "company_name", "company_domain"],
};
export function downloadTemplate(kind: ImportKind) {
  const csv = `${templates[kind].map(csvCell).join(",")}\r\n`;
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${kind}-template.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
export function fileUrl(id: string, which: "original" | "rejected") {
  const ws = /^\/w\/([^/]+)/.exec(window.location.pathname)?.[1];
  return `/api/workspaces/${ws}/imports/${id}/${which}`;
}
